import type { IColumnOption } from '@/components/ControlSidebar/common/AssignEmailColumn/AssignEmailColumn';
import {
  BOARD_FIELD_TYPE,
  DEFAULT_FONT_COLOR,
  DEFAULT_FONT_SIZE,
  FORM_FIELD,
  SignerSettingsColorCode,
  SUPPORTED_COLUMNS_FROM_MONDAY,
} from '@/constants/constants';
import { DEFAULT_DATE_FORMAT } from '@/constants/date-formats';
import { IFileInfo } from '@/interfaces/interfaces';
import { AiDetectionStatus } from '@/types/ai-detection-status';
import {
  IImageGridSettings,
  IPlaceholderField,
} from '@/interfaces/placeholder-field';
import { NumberFormat } from '@/utils/controlSidebar/number-formatting.utils';
import { nanoid } from 'nanoid';
import { StateCreator } from 'zustand';
import { StoreState } from '../pdf-editor.store';
import { pick } from 'lodash';
import { IColumnData, ISubItemSettings } from '@/interfaces/sub-item-settings';
import { calculateColumnWidths } from '@/utils/line-items';
import { createSliceActionsWithGet } from '../utils/createNamedSet';
import {
  applyBulkPatch,
  type BulkFieldPatch,
  isBulkEditableField,
} from '@/utils/bulk-edit-fields';

export interface PlaceholdersSlice {
  files: IFileInfo[];
  hasUnsavedChanges: boolean;
  activeFileId: string;
  paymentFieldAdded: boolean;
  fileSrcMap: Record<string, string>;
  activeField: IPlaceholderField | null;
  /** Fields selected for bulk editing. Empty outside bulk mode. */
  selectedFieldIds: string[];
  fileLinkTop: any;
  mappedFieldIds: Record<string, true>;
  getActiveFile: () => IFileInfo | null | undefined;
  getFileSrc: (fileId: string) => string | undefined;
  actions: PlaceholdersActions;
}

export interface PlaceholdersActions {
  setFiles: (files: IFileInfo[], activeFileId: string) => void;
  setActiveFileId: (activeFileId: string) => void;
  setFileSrc: (fileId: string, src: string) => void;
  setFileFields: (fileId: string, fields: IPlaceholderField[]) => void;
  resetUnsavedChanges: () => void;
  addField: ({ x, y }, field: IPlaceholderField) => void;
  updateField: (field: IPlaceholderField) => void;
  removeField: (field: IPlaceholderField) => void;
  setActiveField: (field: IPlaceholderField) => void;
  clearActiveField: () => void;
  updateFieldPosition: (
    field: IPlaceholderField,
    { x, y }: { x: number; y: number },
  ) => void;
  updateFieldSize: (
    field: IPlaceholderField,
    { width, height }: { width: number; height: number },
  ) => void;
  updateSignerEmailColumn: (columnOption: IColumnOption) => IFileInfo[];
  updateFieldFontStyle: (
    field: IPlaceholderField,
    fontStyle: Partial<IPlaceholderField['fontStyle']>,
  ) => void;
  updateFieldRequired: (field: IPlaceholderField, required: boolean) => void;
  updateFieldColumn: (
    field: IPlaceholderField,
    column: IColumnOption,
    saveIn?: boolean,
  ) => void;
  updateFieldDateFormat: (field: IPlaceholderField, dateFormat: string) => void;
  updateFieldPaymentConfiguration: (
    field: IPlaceholderField,
    paymentConfiguration: {
      currency: string;
      amountColumn: string;
      buttonLabel: string;
    },
  ) => void;
  updateFieldUrlData: (
    field: IPlaceholderField,
    urlData: Partial<{ text: string; url: string }>,
  ) => void;
  updateFieldContent: (field: IPlaceholderField, content: string) => void;
  updateFieldSubItemSettings: (
    field: IPlaceholderField,
    subItemSettings: Partial<
      Omit<ISubItemSettings, 'columnWidth' | 'itemNameColumn' | 'columns'>
    >,
  ) => void;
  updateFieldSubItemSettingsItemNameColumn: (
    field: IPlaceholderField,
    checked: boolean,
  ) => void;
  updateFieldSubItemSettingsColumns: (
    field: IPlaceholderField,
    column: IColumnData,
  ) => void;
  updateFileAiDetectionStatus: (
    fileId: string,
    status: AiDetectionStatus,
  ) => void;
  addMappedFieldIds: (fieldIds: string[]) => void;
  updateFieldImageGridSettings: (
    field: IPlaceholderField,
    imageGridSettings: Partial<IImageGridSettings>,
  ) => void;
  selectFields: (fieldIds: string[]) => void;
  /**
   * Shift-click: adds a field of the active field's type to the bulk selection,
   * or removes it if it is already selected. Returns false when the click does
   * not apply (no active field, a different type, or a type that cannot be bulk
   * edited), so the caller can treat it as a normal click.
   */
  toggleFieldSelection: (field: IPlaceholderField) => boolean;
  clearSelectedFields: () => void;
  bulkUpdateFields: (fieldIds: string[], patch: BulkFieldPatch) => void;
  bulkAssignSigner: (
    fieldIds: string[],
    columnOption: IColumnOption,
  ) => IFileInfo[];
  bulkRemoveFields: (fieldIds: string[]) => void;
}

const initialState: Omit<
  PlaceholdersSlice,
  'actions' | 'getActiveFile' | 'getFileSrc'
> = {
  files: [],
  hasUnsavedChanges: false,
  activeFileId: '',
  paymentFieldAdded: false,
  fileSrcMap: {},
  activeField: null,
  selectedFieldIds: [],
  fileLinkTop: null,
  mappedFieldIds: {},
};

type Slice = StateCreator<
  StoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PlaceholdersSlice
>;

export const createPlaceholdersSlice: Slice = (set, get) => ({
  ...initialState,

  getFileSrc: (fileId: string) => {
    const slice = get().placeholders;
    return slice.fileSrcMap[fileId];
  },

  getActiveFile: () => {
    const slice = get().placeholders;
    if (!slice.activeFileId) return null;
    return slice.files.find((file) => file._id === slice.activeFileId);
  },

  actions: createSliceActionsWithGet(
    set,
    get,
    'placeholders',
    (namedSet, get) => ({
      setFiles: (files: IFileInfo[], activeFileId: string) =>
        namedSet((draft) => {
          const existingFilesMap = new Map(
            draft.placeholders.files.map((f) => [f._id, f]),
          );
          const hasUnsavedChanges = draft.placeholders.hasUnsavedChanges;

          draft.placeholders.files = files.map((file) => {
            const existingFile = existingFilesMap.get(file._id);
            const aiDetectionStatus =
              file.aiDetectionStatus ??
              existingFile?.aiDetectionStatus ??
              AiDetectionStatus.Idle;

            if (hasUnsavedChanges && existingFile) {
              return {
                ...file,
                fields: existingFile.fields,
                pagesAddedByUser: existingFile.pagesAddedByUser,
                aiDetectionStatus,
              };
            }

            return {
              ...file,
              aiDetectionStatus,
            };
          });

          draft.placeholders.activeFileId = activeFileId;
          draft.placeholders.hasUnsavedChanges = hasUnsavedChanges;
          draft.placeholders.paymentFieldAdded = draft.placeholders.files.some(
            (file) =>
              file.fields.some((field) => field.itemId === FORM_FIELD.PAYMENT),
          );
        }),

      setActiveFileId: (activeFileId: string) =>
        namedSet((draft) => {
          draft.placeholders.activeFileId = activeFileId;
          draft.placeholders.selectedFieldIds = [];
          // Reset PDF document state to prevent rendering pages from the previous file
          // The new page count will be set when the new PDF document loads
          draft.pdfDocument.totalPages = 0;
          draft.pdfDocument.hasPageRendered = false;
        }),

      setFileSrc: (fileId: string, src: string) =>
        namedSet((draft) => {
          draft.placeholders.fileSrcMap[fileId] = src;
        }),

      setFileFields: (fileId: string, fields: IPlaceholderField[]) =>
        namedSet((draft) => {
          const file = draft.placeholders.files.find((f) => f._id === fileId);
          if (file) {
            file.fields = fields;
            draft.placeholders.hasUnsavedChanges = true;
          }
        }),

      resetUnsavedChanges: () =>
        namedSet((draft) => {
          draft.placeholders.hasUnsavedChanges = false;
        }),

      addField: ({ x, y }, field: IPlaceholderField) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          draft.placeholders.activeField = field;
          draft.placeholders.selectedFieldIds = [];

          const scale = draft.pdfDocument.canvasRenderDimensions.scale;

          if (!scale || !draft.pdfDocument.pdfIndividualPageDetails) {
            return;
          }

          if (field.itemId === FORM_FIELD.PAYMENT) {
            draft.placeholders.paymentFieldAdded = true;
          }

          const allPageHeights = draft.pdfDocument.pdfIndividualPageDetails.map(
            (x) => x.height * draft.pdfDocument.canvasRenderDimensions.scale,
          );
          const updatedY = draft.placeholders.fileLinkTop ? y - 85 * scale : y;
          let pageNumber = 1;
          let yPositionInPage = updatedY;

          for (let i = 0; i < allPageHeights.length; i++) {
            const pageHeight = allPageHeights[i];
            const adjustedPageHeight = pageHeight + 16;

            if (yPositionInPage > adjustedPageHeight) {
              pageNumber++;
              yPositionInPage -= adjustedPageHeight;
            } else {
              break;
            }
          }

          let scaledX = x / scale;
          let scaledY = yPositionInPage / scale;

          const pageHeight =
            draft.pdfDocument.pdfIndividualPageDetails[pageNumber - 1]?.height;
          const newY = pageHeight - (scaledY % pageHeight);

          const id =
            field.itemId === 'file'
              ? `${field.itemId}-${nanoid()}`
              : `${field.title}-${nanoid()}`;
          const addedField: IPlaceholderField = {
            id,
            ...pick(field, [
              'itemId',
              'title',
              'type',
              'height',
              'width',
              // for date picker
              'format',
              // for sign date
              'dateFormat',
              // for sign date
              'requiresSigner',
              'noPadding',
            ]),
            placeholder: { x: scaledX, y: scaledY },
            isActive: true,
            dir: 'ltr',
            formField: {
              pageNumber: pageNumber,
              pageIndex: pageNumber - 1,
              coordinates: { x: scaledX, y: newY },
            },
            ...(field.itemId === FORM_FIELD.CHECKBOX
              ? { signer: field.signer }
              : {}),
            ...(field.itemId === FORM_FIELD.LINE_ITEM
              ? { subItemSettings: field.subItemSettings }
              : {}),
            ...(field.type === BOARD_FIELD_TYPE.NUMBERS
              ? { formattedValue: field.content }
              : {}),
            ...(field.type === BOARD_FIELD_TYPE.DATE
              ? {
                  dateFormat: {
                    format: DEFAULT_DATE_FORMAT,
                    label: DEFAULT_DATE_FORMAT,
                    value: 0,
                  },
                }
              : {}),
            ...(field.type === BOARD_FIELD_TYPE.FILE
              ? {
                  imageGridSettings: {
                    columns: field.imageGridSettings?.columns ?? 1,
                    rows: field.imageGridSettings?.rows ?? 1,
                  },
                }
              : {}),
            fontStyle: {
              decimalPlaces: 3,
              numberFormat: NumberFormat.COMMA_DOT,
              bold: false,
              italics: false,
              alignment: 'left',
              fontSize: DEFAULT_FONT_SIZE,
              color: DEFAULT_FONT_COLOR,
              ...field.fontStyle,
            },
          };

          draft.placeholders.files = draft.placeholders.files.map((f) => ({
            ...f,
            fields: f.fields.map((field) => ({ ...field, isActive: false })),
          }));

          draft.placeholders.files
            .find((f) => f._id === draft.placeholders.activeFileId)
            ?.fields.push(addedField);
          draft.placeholders.activeField = addedField;
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldPosition: (
        field: IPlaceholderField,
        { x, y }: { x: number; y: number },
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const scale = draft.pdfDocument.canvasRenderDimensions.scale;
          const pdfIndividualPageDetails =
            draft.pdfDocument.pdfIndividualPageDetails;
          const allPageHeights = pdfIndividualPageDetails.map(
            (x) => x.height * scale,
          );
          const updatedY = draft.placeholders.fileLinkTop ? y - 85 * scale : y;
          let pageNumber = 1;
          let yPositionInPage = updatedY;

          for (let i = 0; i < allPageHeights.length; i++) {
            const pageHeight = allPageHeights[i];
            const adjustedPageHeight = pageHeight + 16;

            if (yPositionInPage > adjustedPageHeight) {
              pageNumber++;
              yPositionInPage -= adjustedPageHeight;
            } else {
              break;
            }
          }

          let scaledX = x / scale;
          let scaledY = yPositionInPage / scale;

          const pageHeight = pdfIndividualPageDetails[pageNumber - 1]?.height;
          const newY = pageHeight - (scaledY % pageHeight);
          const existingField = draft.placeholders.files
            .find((f) => f._id === draft.placeholders.activeFileId)
            ?.fields.find((f) => f.id === field.id);
          if (!existingField) return;
          existingField.placeholder = { x: scaledX, y: scaledY };
          existingField.formField = {
            pageNumber: pageNumber,
            pageIndex: pageNumber - 1,
            coordinates: { x: scaledX, y: newY },
          };
          if (draft.placeholders.activeField?.id === field.id) {
            draft.placeholders.activeField = existingField;
          }
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateField: (field: IPlaceholderField) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          updateField(draft, field);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      removeField: (field: IPlaceholderField) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          draft.placeholders.files = draft.placeholders.files.map((f) => ({
            ...f,
            fields: f.fields.filter((f) => f.id !== field.id),
          }));
          draft.placeholders.activeField = null;
          draft.placeholders.selectedFieldIds = [];
          draft.placeholders.paymentFieldAdded = false;
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      setActiveField: (field: IPlaceholderField) =>
        namedSet((draft) => {
          // Clicking the field that is already active (as happens when a resize
          // drag ends on it) keeps a bulk selection; activating another field
          // ends it.
          if (draft.placeholders.activeField?.id !== field.id) {
            draft.placeholders.selectedFieldIds = [];
          }
          if (draft.placeholders.activeField) {
            const prevActiveFieldId = draft.placeholders.activeField.id;
            draft.placeholders.files = draft.placeholders.files.map((f) => ({
              ...f,
              fields: f.fields.map((existingField) => {
                if (existingField.id === prevActiveFieldId) {
                  return { ...existingField, isActive: false };
                }
                return existingField;
              }) as IPlaceholderField[],
            }));
          }

          draft.placeholders.files = draft.placeholders.files.map((f) => ({
            ...f,
            fields: f.fields.map((existingField) => {
              if (existingField.id === field.id) {
                return { ...existingField, isActive: true };
              }
              return existingField;
            }) as IPlaceholderField[],
          }));

          draft.placeholders.activeField = { ...field, isActive: true };
        }),

      clearActiveField: () =>
        namedSet((draft) => {
          if (draft.placeholders.activeField) {
            const activeFieldId = draft.placeholders.activeField.id;
            draft.placeholders.files = draft.placeholders.files.map((f) => ({
              ...f,
              fields: f.fields.map((existingField) => {
                if (existingField.id === activeFieldId) {
                  return { ...existingField, isActive: false };
                }
                return existingField;
              }) as IPlaceholderField[],
            }));
          }
          draft.placeholders.activeField = null;
          draft.placeholders.selectedFieldIds = [];
        }),

      updateSignerEmailColumn: (columnOption: IColumnOption): IFileInfo[] => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = {
            ...draft.placeholders.activeField,
            signer: {
              value: columnOption.value,
              label: columnOption.label,
              color: columnOption.color || '',
              emailColumnId: columnOption.emailColumnId || '',
              type: columnOption.type,
              userId: columnOption.userId,
            },
            color: columnOption.color as SignerSettingsColorCode,
          };

          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();

        return get().placeholders.files;
      },

      updateFieldSize: (
        field: IPlaceholderField,
        { width, height }: { width: number; height: number },
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const { selectedFieldIds } = draft.placeholders;
          // Resizing a field of a bulk selection resizes every selected field.
          // Only the dimension that was dragged is copied, so dragging the
          // right edge leaves each field's own height as it was.
          if (
            selectedFieldIds.length > 1 &&
            selectedFieldIds.includes(field.id)
          ) {
            const patch: BulkFieldPatch = {};
            if (width !== field.width) patch.width = width;
            if (height !== field.height) patch.height = height;
            const pages = draft.pdfDocument.pdfIndividualPageDetails;
            updateFields(draft, new Set(selectedFieldIds), (selected) =>
              applyBulkPatch(
                selected,
                patch,
                pages?.[selected.formField.pageIndex],
              ),
            );
            return;
          }
          const updatedField = { ...field, width, height };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldFontStyle: (
        field: IPlaceholderField,
        fontStyle: Partial<{
          bold: boolean;
          italics: boolean;
          fontSize: number;
          color: { r: number; g: number; b: number };
          alignment: string;
        }>,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = {
            ...field,
            fontStyle: { ...field.fontStyle, ...fontStyle },
          };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldRequired: (field: IPlaceholderField, required: boolean) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = { ...field, required };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldColumn: (
        field: IPlaceholderField,
        column: IColumnOption,
        saveIn?: boolean,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          // For dropdown fields, when saveIn is true, use saveInColumn instead of column
          if (saveIn && field.itemId === FORM_FIELD.DROPDOWN) {
            const updatedField: IPlaceholderField = {
              ...field,
              saveInColumn: column,
              saveIn,
            };
            updateField(draft, updatedField);
          } else if (
            field.itemId !== FORM_FIELD.DROPDOWN &&
            (field.type === BOARD_FIELD_TYPE.FILE ||
              (typeof field.type === 'string' &&
                SUPPORTED_COLUMNS_FROM_MONDAY.some((s) => s === field.type)))
          ) {
            const updatedField: IPlaceholderField = {
              ...field,
              column,
              saveIn,
              itemId: column ? String(column.value ?? '') : '',
              title: column?.label ?? field.title,
            };
            updateField(draft, updatedField);
          } else {
            const updatedField: IPlaceholderField = {
              ...field,
              column,
              saveIn,
              ...(field.type === BOARD_FIELD_TYPE.FILE
                ? {
                    itemId: column ? String(column.value ?? '') : '',
                  }
                : {}),
            };
            updateField(draft, updatedField);
          }
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldDateFormat: (field: IPlaceholderField, dateFormat: string) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          let updatedField: IPlaceholderField;
          if (
            field.itemId === FORM_FIELD.SIGN_DATE ||
            field.type === BOARD_FIELD_TYPE.DATE
          ) {
            updatedField = {
              ...field,
              dateFormat: { format: dateFormat, label: dateFormat },
            };
          } else {
            updatedField = {
              ...field,
              format: { format: dateFormat, label: dateFormat },
            };
          }
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldPaymentConfiguration: (
        field: IPlaceholderField,
        paymentConfiguration: {
          currency: string;
          amountColumn: string;
          buttonLabel: string;
        },
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = {
            ...field,
            currency: paymentConfiguration.currency,
            amountColumn: paymentConfiguration.amountColumn,
            text: paymentConfiguration.buttonLabel,
          };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldUrlData: (
        field: IPlaceholderField,
        urlData: Partial<{ text: string; url: string }>,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = {
            ...field,
            urlData: { ...field.urlData, ...urlData },
          };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldContent: (field: IPlaceholderField, content: string) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField = { ...field, content };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldSubItemSettings: (
        field: IPlaceholderField,
        subItemSettings: Partial<Omit<ISubItemSettings, 'columnWidth'>>,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const draftField = draft.placeholders.files
            .flatMap((f) => f.fields)
            .find((f) => f.id === field.id);
          if (!draftField) return;

          draftField.subItemSettings = {
            ...draftField.subItemSettings,
            ...subItemSettings,
          };
          draft.placeholders.activeField = draftField;
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldSubItemSettingsItemNameColumn: (
        field: IPlaceholderField,
        checked: boolean,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const draftField = draft.placeholders.files
            .flatMap((f) => f.fields)
            .find((f) => f.id === field.id);
          if (!draftField) return;
          draftField.subItemSettings.itemNameColumn.checked = checked;
          draftField.subItemSettings.columnWidth = calculateColumnWidths(
            draftField.subItemSettings,
          );
          draft.placeholders.activeField = draftField;
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFieldSubItemSettingsColumns: (
        field: IPlaceholderField,
        column: IColumnData,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const draftField = draft.placeholders.files
            .flatMap((f) => f.fields)
            .find((f) => f.id === field.id);
          if (!draftField) return;
          const columnIndex = draftField.subItemSettings.columns.findIndex(
            (col) => col.id === column.id,
          );
          if (columnIndex === -1) {
            draftField.subItemSettings.columns.push({
              ...column,
              checked: true,
            });
          } else {
            draftField.subItemSettings.columns.splice(columnIndex, 1);
          }

          draftField.subItemSettings.columnWidth = calculateColumnWidths(
            draftField.subItemSettings,
          );
          draft.placeholders.activeField = draftField;
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      updateFileAiDetectionStatus: (
        fileId: string,
        status: AiDetectionStatus,
      ) =>
        namedSet((draft) => {
          const file = draft.placeholders.files.find((f) => f._id === fileId);
          if (file) {
            file.aiDetectionStatus = status;
          }
        }),

      addMappedFieldIds: (fieldIds: string[]) =>
        namedSet((draft) => {
          for (const id of fieldIds) {
            draft.placeholders.mappedFieldIds[id] = true;
          }
        }),

      updateFieldImageGridSettings: (
        field: IPlaceholderField,
        imageGridSettings: Partial<IImageGridSettings>,
      ) => {
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const updatedField: IPlaceholderField = {
            ...field,
            imageGridSettings: {
              columns: 1,
              rows: 1,
              ...(field.imageGridSettings ?? {}),
              ...imageGridSettings,
            },
          };
          updateField(draft, updatedField);
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      toggleFieldSelection: (field: IPlaceholderField) => {
        const { activeField: anchor, selectedFieldIds } = get().placeholders;
        if (
          !anchor ||
          !isBulkEditableField(anchor) ||
          field.itemId !== anchor.itemId
        ) {
          return false;
        }
        // The active field anchors the selection, so shift-clicking it again
        // leaves the selection as it is.
        if (field.id === anchor.id) return true;

        namedSet((draft) => {
          const current = selectedFieldIds.length
            ? selectedFieldIds
            : [anchor.id];
          const next = current.includes(field.id)
            ? current.filter((id) => id !== field.id)
            : [...current, field.id];
          // A single field is ordinary editing, not bulk editing.
          draft.placeholders.selectedFieldIds = next.length > 1 ? next : [];
        });
        return true;
      },

      selectFields: (fieldIds: string[]) =>
        namedSet((draft) => {
          draft.placeholders.selectedFieldIds = [...new Set(fieldIds)];
        }),

      clearSelectedFields: () =>
        namedSet((draft) => {
          draft.placeholders.selectedFieldIds = [];
        }),

      bulkUpdateFields: (fieldIds: string[], patch: BulkFieldPatch) => {
        if (!fieldIds.length) return;
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const ids = new Set(fieldIds);
          const pages = draft.pdfDocument.pdfIndividualPageDetails;
          updateFields(draft, ids, (field) =>
            applyBulkPatch(field, patch, pages?.[field.formField.pageIndex]),
          );
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },

      bulkAssignSigner: (
        fieldIds: string[],
        columnOption: IColumnOption,
      ): IFileInfo[] => {
        if (!fieldIds.length) return get().placeholders.files;
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          updateFields(draft, new Set(fieldIds), (field) => ({
            ...field,
            signer: {
              value: columnOption.value,
              label: columnOption.label,
              color: columnOption.color || '',
              emailColumnId: columnOption.emailColumnId || '',
              type: columnOption.type,
              userId: columnOption.userId,
            },
            color: columnOption.color as SignerSettingsColorCode,
          }));
        });
        get().placeholdersHistory.historyActions.commitHistory();

        return get().placeholders.files;
      },

      bulkRemoveFields: (fieldIds: string[]) => {
        if (!fieldIds.length) return;
        get().placeholdersHistory.historyActions.recordHistory();
        namedSet((draft) => {
          const ids = new Set(fieldIds);
          draft.placeholders.files = draft.placeholders.files.map((f) => ({
            ...f,
            fields: f.fields.filter((field) => !ids.has(field.id)),
          }));
          draft.placeholders.activeField = null;
          draft.placeholders.selectedFieldIds = [];
          draft.placeholders.paymentFieldAdded = draft.placeholders.files.some(
            (file) =>
              file.fields.some((field) => field.itemId === FORM_FIELD.PAYMENT),
          );
          draft.placeholders.hasUnsavedChanges = true;
        });
        get().placeholdersHistory.historyActions.commitHistory();
      },
    }),
  ),
});

function updateField(draft: StoreState, updatedField: IPlaceholderField) {
  draft.placeholders.files = draft.placeholders.files.map((f) => {
    return {
      ...f,
      fields: f.fields.map((field) => {
        if (field.id === updatedField?.id) {
          return updatedField;
        }
        return field;
      }) as IPlaceholderField[],
    };
  });
  draft.placeholders.activeField = updatedField;
  draft.placeholders.hasUnsavedChanges = true;
}

/**
 * Updates many fields in one pass. Keeps activeField in step when it is one of
 * them, so the sidebar shows the new values.
 */
function updateFields(
  draft: StoreState,
  ids: Set<string>,
  update: (field: IPlaceholderField) => IPlaceholderField,
) {
  draft.placeholders.files = draft.placeholders.files.map((f) => ({
    ...f,
    fields: f.fields.map((field) =>
      ids.has(field.id) ? update(field) : field,
    ) as IPlaceholderField[],
  }));
  const activeId = draft.placeholders.activeField?.id;
  if (activeId && ids.has(activeId)) {
    draft.placeholders.activeField =
      draft.placeholders.files
        .flatMap((f) => f.fields)
        .find((field) => field.id === activeId) ?? null;
  }
  draft.placeholders.hasUnsavedChanges = true;
}
